import type { TableCartSnapshot } from "./table"
import type * as Product from "./types/product"
import type * as Table from "./types/table"
import type { SensitiveApprovalRequestConfig } from "@/utils/service"
import { REPO_MOCK_SERVER_URL, request } from "@/utils/service"
import {
  buildOrderRequestFingerprint,
  clearOrderRequestId,
  getOrCreateOrderRequestId,
} from "./order-idempotency"
import { DEVICE_MANAGE_PRINTER_API_MOCK } from "./printing"

const PREFIX = "teashop_merchant/"

/** 获取某店铺的商品类目(含商品)列表 */
export function getCategoryAndProductListByShopId(shopId: number) {
  return request<Product.ProductCategoryAndProductTListData>({
    url: `${PREFIX}merchant/pms/item/category/list_by_shop_id/${shopId}`,
    method: "GET",
    mock: {
      mock: DEVICE_MANAGE_PRINTER_API_MOCK,
      mockUrl: REPO_MOCK_SERVER_URL,
    },
  })
}

/** 获取某店铺商品一二级分类树（含商品，商品只挂二级节点） */
export function getCategoryTreeWithProductListByShopId(shopId: number) {
  return request<Product.ProductCategoryAndProductTListData>({
    url: `${PREFIX}merchant/pms/item/category/tree_with_products/${shopId}`,
    method: "GET",
  })
}

/** 获取某店铺的商品类目列表 */
export function getCategoryListByShopId(shopId: number) {
  return request<Product.ProductCategoryListData>({
    url: `${PREFIX}merchant/pms/item/category/${shopId}`,
    method: "GET",
  })
}

/** 通过商品类目id获取商品列表 */
export function getProductListByCategoryId(categoryId: number) {
  return request<Product.ProductListData>({
    url: `${PREFIX}merchant/pms/item/category/itemList/${categoryId}`,
    method: "GET",
  })
}

/**
 * 从商品导入创建成本卡
 */

export function importCostCardFromItem(data: Product.ItemForCostCardType[]) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/wms/costCard/item/import`,
    method: "POST",
    data,
  })
}

/** 通过商品类目id获取商品成本卡列表 */
export function getProductCostCardListByCategoryId(categoryId: number) {
  return request<Product.ProductCostCardListData>({
    url: `${PREFIX}merchant/wms/costCard/itemList/${categoryId}`,
    method: "GET",
  })
}

/** 通过商品类目id获取商品成本卡列表V2 */
export function getProductCostCardListByCategoryIdV2(categoryId: number) {
  return request<Product.ProductCostCardListData>({
    url: `${PREFIX}merchant/wms/costCard/itemList/V2/${categoryId}`,
    method: "GET",
  })
}

/** 通过item_id获取成本卡 */
export function getProductCostCardByItemId(itemId: number) {
  return request<Product.ProductCostCardData>({
    url: `${PREFIX}merchant/wms/costCard/${itemId}`,
    method: "GET",
  })
}

/** 创建桌台商品子订单 */
export function createItemOrder(
  data: Product.ItemOrderType,
  cartSnapshot?: Pick<TableCartSnapshot, "scopeEpoch" | "revision">,
) {
  const { requestId: suppliedRequestId, ...materialPayload } = data
  const fingerprint = buildOrderRequestFingerprint("table", materialPayload)
  const requestId = suppliedRequestId || getOrCreateOrderRequestId(fingerprint)
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/create_2b_v2`,
    method: "POST",
    data: {
      ...data,
      requestId,
    },
    headers: cartSnapshot
      ? {
          "X-Table-Cart-Scope-Epoch": cartSnapshot.scopeEpoch,
          ...(cartSnapshot.revision ? { "X-Table-Cart-Revision": cartSnapshot.revision } : {}),
        }
      : undefined,
  }).then((response) => {
    if (response.code === 0 && !suppliedRequestId) clearOrderRequestId(fingerprint)
    return { ...response, requestId }
  })
}

/** 更新桌台商品子订单 */
export function updateItemOrder(data: Product.ItemOrderType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order`,
    method: "PUT",
    data,
  })
}

/** 通过桌台记录号获取桌台商品子订单 */
export function getItemOrderByTableRecordId(tableRecordId: number) {
  return request<Product.ProductItemOrderListData>({
    url: `${PREFIX}merchant_oms_item_order/list_by_table_record_id/${tableRecordId}`,
    method: "GET",
  })
}

export interface ItemOrderSplitRequest {
  requestId: string
  expectedCount: number
  confirmCancelPendingApproval: boolean
}

export interface ItemOrderSplitResponse {
  status: "CONFIRM_REQUIRED" | "APPROVAL_EXECUTING" | "SPLIT_COMPLETED"
  itemOrderId: string | number
  sourceItemLineId: string
  itemLines: Array<Record<string, any>>
  skuTotalNumber?: number
  totalAmount?: number
  canceledApprovalTicketIds?: number[]
}

/** 持久化拆分已下单商品行；接口本身不会触发打印。 */
export function splitItemOrderLine(itemOrderId: string | number, itemLineId: string, data: ItemOrderSplitRequest) {
  return request<IApiResponseData<ItemOrderSplitResponse>>({
    url: `${PREFIX}merchant_oms_item_order/${itemOrderId}/item-lines/${encodeURIComponent(itemLineId)}/splits`,
    method: "POST",
    data,
  })
}

/** 商品单独设置提成人 */
export function setItemCommissionUser(data: Product.SetItemCommissionUserDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/set_item_commission_user`,
    method: "POST",
    data,
  })
}

/** 批量设置商品提成人 */
export function setItemCommissionUsers(data: Product.SetItemCommissionUserBatchDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/set_item_commission_users`,
    method: "POST",
    data,
  })
}

/** 预览待下单商品提成 */
export function previewProductCommission(data: Product.ProductCommissionPreviewDTO) {
  return request<Product.ProductCommissionPreviewData>({
    url: `${PREFIX}merchant_oms_item_order/preview_product_commission`,
    method: "POST",
    data,
  })
}

/**
 * 桌台商品转台
 * @deprecated 废弃
 */
export function transferItemOrder(data: Product.TransferItemOrderType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant/table/product/transfer`,
    method: "POST",
    data,
  })
}

/** 桌台商品取消 */
export function cancelItemOrder(data: Product.CancelItemOrderType & SensitiveApprovalRequestConfig) {
  const {
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
    itemOrderId,
    remarks,
    requestId,
    authPassword,
    itemOrder,
  } = data
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/cancel`,
    method: "POST",
    data: {
      itemOrderId,
      remarks,
      requestId: requestId || createCancelRequestId(),
      authPassword,
      itemOrder: {
        itemId: itemOrder.itemId,
        ...(itemOrder.itemLineId ? { itemLineId: itemOrder.itemLineId } : {}),
        count: itemOrder.count,
      },
    },
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
  })
}

export function createCancelRequestId() {
  const randomPart = globalThis.crypto?.randomUUID?.()
    || Math.random().toString(36).slice(2)
  return `cancel-${Date.now().toString(36)}-${randomPart}`.slice(0, 64)
}

const pendingCancelRequestIds = new Map<string, string>()
const CANCEL_REQUEST_STORAGE_PREFIX = "tea-shop-2b:pending-cancel:"

function currentSessionStorage(): Storage | undefined {
  try {
    return typeof sessionStorage === "undefined" ? undefined : sessionStorage
  }
  catch {
    return undefined
  }
}

function cancelRequestStorageKey(fingerprint: string) {
  let first = 0x811C9DC5
  let second = 0x9E3779B9
  for (let index = 0; index < fingerprint.length; index += 1) {
    const code = fingerprint.charCodeAt(index)
    first = Math.imul(first ^ code, 0x01000193)
    second = Math.imul(second ^ (code + index), 0x85EBCA77)
  }
  return `${CANCEL_REQUEST_STORAGE_PREFIX}${fingerprint.length}-${(first >>> 0).toString(16)}-${(second >>> 0).toString(16)}`
}

export function getOrCreateCancelRequestId(fingerprint: string) {
  const key = cancelRequestStorageKey(fingerprint)
  let persisted: string | null | undefined
  try {
    persisted = currentSessionStorage()?.getItem(key)
  }
  catch {
    persisted = undefined
  }
  const existing = persisted || pendingCancelRequestIds.get(key)
  if (existing) return existing
  const requestId = createCancelRequestId()
  pendingCancelRequestIds.set(key, requestId)
  try {
    currentSessionStorage()?.setItem(key, requestId)
  }
  catch {
    // sessionStorage 不可用时保留模块级幂等键。
  }
  return requestId
}

export function clearCancelRequestId(fingerprint: string) {
  const key = cancelRequestStorageKey(fingerprint)
  pendingCancelRequestIds.delete(key)
  try {
    currentSessionStorage()?.removeItem(key)
  }
  catch {
    // 清理持久层失败不影响当前模块移除已完成请求。
  }
}

/** 商品转台 */
export function transferProduct(data: Product.TransferItemOrderVO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/transfer`,
    method: "POST",
    data,
  })
}

/** 商品转赠 */
export function transferGift(data: Product.ItemOrderActionTargetType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/transfer_gift`,
    method: "POST",
    data,
  })
}

/** 商品取消转赠 */
export function cancelTransferGift(data: Product.ItemOrderActionTargetType) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/cancel_transfer_gift`,
    method: "POST",
    data,
  })
}

/** 商品单独指定折扣 */
export function discountSpecItem(data: Product.DiscountSpecItemType & SensitiveApprovalRequestConfig) {
  const {
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
    ...requestData
  } = data
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/item/spec/discount`,
    method: "POST",
    data: requestData,
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
  })
}

/** 商品单独打折业务权限检查 */
export function checkDiscountSpecItemPermissions(data: Table.ItemSpecifiedDiscountInfoDTO) {
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/item/spec/discount/check`,
    method: "POST",
    data,
  })
}

/** 商品单独取消折扣 */
export function cancelDiscountSpecItem(data: Product.CancelDiscountSpecItemType & SensitiveApprovalRequestConfig) {
  const {
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
    ...params
  } = data
  return request<IApiResponseData<boolean>>({
    url: `${PREFIX}merchant_oms_item_order/item/spec/cancel`,
    method: "POST",
    params,
    sensitiveApprovalBusinessName,
    sensitiveApprovalDetailRows,
    sensitiveApprovalReason,
  })
}
